import { useState, useRef, useEffect } from 'react';
import type { SelectOption } from './MobileMultipleSelect';
import * as S from './styles';
import { ChevronDownIcon } from '../../../../../assets/icons/ChevronDownIcon';
import ReactDOM from 'react-dom';
import CheckIcon from '../../../../../assets/icons/CheckIcon';

interface DesktopMultipleSelectProps {
  options: SelectOption[];
  values: string[];
  onChange: (value: string[]) => void;
  title?: string;
  width?: string | number;
}

export const DesktopMultipleSelect = ({
  options,
  values: value,
  onChange,
  title = 'Select an option',
  width,
}: DesktopMultipleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const optionListRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyle({
          position: 'absolute',
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
          zIndex: 2000,
        });
      }
    }, 0);
  };
  const handleClose = () => setIsOpen(false);

  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
    // 드롭다운을 닫지 않음
  };

  // 외부 클릭 시 닫힘
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionListRef.current &&
        !optionListRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        ...(width ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
      }}
    >
      <S.SelectButton ref={buttonRef} onClick={handleOpen} type="button">
        {title}
        <span>
          <ChevronDownIcon isOpen={isOpen} />
        </span>
      </S.SelectButton>
      {isOpen &&
        ReactDOM.createPortal(
          <S.MultipleOptionList ref={optionListRef} style={dropdownStyle}>
            {options.map((option, idx) => (
              <>
                <S.MultipleOptionItem
                  key={option.value}
                  isSelected={value.includes(option.value)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option.value);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  type="button"
                  tabIndex={-1}
                >
                  <S.MultipleOptionLabel>{option.label}</S.MultipleOptionLabel>
                  <S.MultipleOptionCheckbox checked={value.includes(option.value)}>
                    <CheckIcon />
                  </S.MultipleOptionCheckbox>
                </S.MultipleOptionItem>
                {idx < options.length - 1 && <S.MultipleOptionDivider />}
              </>
            ))}
          </S.MultipleOptionList>,
          document.body,
        )}
    </div>
  );
};
